import { useState, useEffect, type FormEvent } from "react";
import { Modal } from "@/components/base/Modal";
import { Field } from "@/components/base/Field";
import { TextField } from "@/components/base/TextField";
import { Select } from "@/components/base/Select";
import { Toggle } from "@/components/base/Toggle";
import { Button } from "@/components/base/Button";
import { usersService } from "@/services";
import type { Role, StaffUser } from "@/services/types";
import { ROLES } from "@/config/roles";
import { useToast } from "@/context/ToastContext";

interface UserFormModalProps {
  open: boolean;
  onClose: () => void;
  initial: StaffUser | null;
  users: StaffUser[];
  currentUserId: string;
  actor: string;
  onSaved: () => void;
}

export function UserFormModal({ open, onClose, initial, users, currentUserId, actor, onSaved }: UserFormModalProps) {
  const toast = useToast();
  const [name, setName] = useState(initial?.name ?? "");
  const [email, setEmail] = useState(initial?.email ?? "");
  const [phone, setPhone] = useState(initial?.phone ?? "");
  const [role, setRole] = useState<Role>(initial?.role ?? "collection_agent");
  const [status, setStatus] = useState<"active" | "inactive">(initial?.status ?? "active");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName(initial?.name ?? "");
      setEmail(initial?.email ?? "");
      setPhone(initial?.phone ?? "");
      setRole(initial?.role ?? "collection_agent");
      setStatus(initial?.status ?? "active");
      setPassword("");
      setShowPassword(false);
      setError(null);
    }
  }, [open, initial]);

  const isSelf = initial?.id === currentUserId;
  const isLastSuperAdmin =
    initial?.role === "super_admin" &&
    initial.status === "active" &&
    users.filter((u) => u.role === "super_admin" && u.status === "active" && u.id !== initial.id).length === 0;

  const demotingSuperAdmin = isLastSuperAdmin && role !== "super_admin";
  const deactivatingSuperAdmin = isLastSuperAdmin && status === "inactive";

  const canChangeRole = !isSelf && !demotingSuperAdmin;
  const canChangeStatus = !isSelf && !deactivatingSuperAdmin;

  const generateRandomPassword = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%";
    let gen = "";
    for (let i = 0; i < 10; i++) gen += chars[Math.floor(Math.random() * chars.length)];
    setPassword(gen);
    setShowPassword(true);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setError(null);
    if (!name.trim()) return setError("Full name is required.");
    if (!email.trim()) return setError("Email is required.");
    if (!phone.trim()) return setError("Phone number is required.");
    if (password.trim() && password.trim().length < 6) {
      return setError("Password must be at least 6 characters.");
    }
    if (demotingSuperAdmin) return setError("Cannot demote the last active Super Admin.");
    if (deactivatingSuperAdmin) return setError("Cannot deactivate the last active Super Admin.");

    setSaving(true);
    try {
      if (initial) {
        await usersService.updateUser(
          initial.id,
          { name, email, phone, role, password: password.trim() || undefined },
          actor,
          currentUserId
        );
      } else {
        await usersService.createUser(
          { name, email, phone, role, status, password: password.trim() || undefined },
          actor
        );
      }
      toast.success(
        initial ? "User updated" : "User created",
        password.trim()
          ? `${name.trim()} was saved with updated password.`
          : `${name.trim()} was saved successfully.`
      );
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save the user.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={initial ? "Edit user" : "Add user"}
      description={initial ? `Edit ${initial.name}'s account details.` : "Create a new staff account."}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" form="user-form" loading={saving} icon={<i className="ri-save-line" />}>
            {initial ? "Save changes" : "Create user"}
          </Button>
        </>
      }
    >
      <form id="user-form" onSubmit={submit} className="space-y-4">
        {error ? (
          <div className="flex items-start gap-2 rounded-md border border-danger-200 bg-danger-50 px-3 py-2 text-sm text-danger-700">
            <i className="ri-error-warning-line mt-0.5 flex-none text-base" />
            <span>{error}</span>
          </div>
        ) : null}

        {isSelf ? (
          <div className="rounded-md bg-background-100/60 px-3 py-2.5 text-xs text-foreground-600">
            This is your own account. You cannot change your role or deactivate yourself here — ask another Super Admin
            to make those changes.
          </div>
        ) : null}
        {isLastSuperAdmin ? (
          <div className="rounded-md bg-warning-50 px-3 py-2.5 text-xs text-warning-800">
            This is the last active Super Admin. You cannot demote or deactivate them, or you would lock everyone out of
            the system.
          </div>
        ) : null}

        <TextField label="Full name" value={name} onChange={(e) => setName(e.target.value)} required />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <TextField label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          <TextField label="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} required />
        </div>

        <div>
          <div className="mb-1 flex items-center justify-between">
            <label className="text-xs font-medium text-foreground-700">
              {initial ? "Change password" : "Password"}
            </label>
            <button
              type="button"
              onClick={generateRandomPassword}
              className="text-xs font-medium text-primary-600 hover:text-primary-700 hover:underline cursor-pointer"
            >
              Generate random
            </button>
          </div>
          <TextField
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={
              initial
                ? "Leave blank to keep existing password"
                : "Enter password (or leave blank to auto-generate)"
            }
            helper={
              initial
                ? "Enter a new password (min 6 characters) only if you want to change it for this user."
                : "Leave blank to automatically generate a secure temporary password."
            }
            rightIcon={showPassword ? "ri-eye-off-line" : "ri-eye-line"}
            onRightIconClick={() => setShowPassword((v) => !v)}
          />
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Role" required>
            <Select
              value={role}
              onChange={(v) => setRole(v as Role)}
              disabled={!canChangeRole}
              options={ROLES.map((r) => ({ value: r.value, label: r.label }))}
            />
          </Field>
          <Field label="Status">
            <div className="flex h-9 items-center">
              <Toggle
                checked={status === "active"}
                onChange={(v) => setStatus(v ? "active" : "inactive")}
                disabled={!canChangeStatus}
                label={status === "active" ? "Active" : "Inactive"}
                size="sm"
              />
            </div>
          </Field>
        </div>
        {!canChangeRole && !isSelf ? (
          <p className="text-xs text-danger-600">Role is locked — demoting the last Super Admin is not allowed.</p>
        ) : null}
      </form>
    </Modal>
  );
}